import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import * as AIAssistantActions from './ai-assistant.actions';
import {
  AiFunctionCallControllerService,
  AiFunctionCall, ProjectChannelControllerService, ProjectChannelMessageControllerService,
  ProjectChannel
} from '@rappider/rappider-sdk';
import { Store } from '@ngrx/store';
import { NzMessageService } from 'ng-zorro-antd/message';
import { selectActiveProjectId } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';
import { Router } from '@angular/router';
import { of } from 'rxjs';
// import { UnsynchronizedMessage } from '../utils/unsynchronized-message.interface';
import * as AuthenticationActions from 'libs/authentication/src/lib/state/authentication.actions';
import { ProjectChannelType } from '../utils/ai-assistant.enums';
import { NotificationService } from 'libs/components/src/lib/services';

export const RAPIDER_AI_RECEIVER_ID = '00000000-0000-0000-0000-000000000001'; // This is a constant for the AI receiver ID, used for direct messages

@Injectable()
export class AIAssistantEffects {
  activePollingKey?: string = undefined;

  defaultProjectChannel: ProjectChannel | undefined;

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private nzMessageService: NzMessageService,
    private router: Router,
    private aiFunctionCallControllerService: AiFunctionCallControllerService,
    private projectChannelMessageControllerService: ProjectChannelMessageControllerService,
    private projectChannelControllerService: ProjectChannelControllerService,
    private notificationService: NotificationService
  ) { }


  /**
     * This effect handles the creation of a chat message.
     *
     * It follows this logic:
     * 1.  Check if a default channel exists in the NgRx store's state.
     * 2.  If it exists, use its ID to create the message.
     * 3.  If it doesn't exist in the state, call the API to find a default channel.
     * 4.  If the API finds one, use its ID and update the store.
     * 5.  If the API doesn't find one, create a new default channel, use its ID, and update the store.
     * 6.  Any errors in the process will dispatch an error action.
     */
  createChannelMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AIAssistantActions.CreateChannelMessage),
      // Get the latest channel list from the store.
      withLatestFrom(this.store.select((state) => state.aiAssistant?.channels)),
      // Use switchMap to handle the main asynchronous logic.
      // This ensures that if a new action comes in, the previous (and now outdated) process is cancelled.
      switchMap(([action, channelsInState]) => {
        const defaultChannelInState = channelsInState?.find((c: ProjectChannel) => c.isDefault);
        // If a default channel exists in the state, use it.
        const channel$ = of({ id: undefined, isDefault: true, type: ProjectChannelType.PublicChannel } as unknown as ProjectChannel);
        // Now that we have a reliable stream (`channel$`) that will provide the channel,
        // we can chain the final step: creating the message.
        return channel$.pipe(
          switchMap((channel: ProjectChannel) =>
            // This is the primary operation, executed with the correct channelId.
            this.projectChannelMessageControllerService
              .sendChannelMessage({
                body: {
                  message: action.payload?.message?.message ?? '',
                  type: action.payload.message?.type,
                  status: 'new',
                  data: action.payload?.message?.data,
                  config: action.payload?.message?.config,
                  metadata: action.payload?.message?.metadata,
                  channelId: channel?.id,
                  aiFunctionId: action.payload?.message?.aiFunctionId,
                },
              })
              .pipe(
                // On success, map the API response to the success action.
                map((message: any) => AIAssistantActions.GetChannelMessagesByTimestamp({
                  payload: { timestamp: new Date().toISOString() }
                })),
                // Catch any errors from the message creation API call.
                catchError((error) =>
                  of(
                    AIAssistantActions.Error({
                      payload: { error: { error, key: 'CreateChannelMessage' } },
                    })
                  )
                )
              )
          ),
          // Catch any errors from the channel finding/creating logic.
          catchError((error) =>
            of(
              AIAssistantActions.Error({
                payload: { error: { error, key: 'FindOrCreateChannel' } },
              })
            )
          )
        );
      })
    )
  );


  getDirectMessagesByTimestamp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AIAssistantActions.GetDirectMessagesByTimestamp),
      withLatestFrom(this.store.select(selectActiveProjectId)),
      mergeMap(([action, activeProjectId]) => {
        const receiverId = action.payload?.receiverId || RAPIDER_AI_RECEIVER_ID;
        const filter = {
          where: {
            createdDate: {
              lte: action.payload?.timestamp || new Date().toISOString()
            }
          },
          order: ['createdDate DESC'],
          limit: 20
        };
        return this.projectChannelMessageControllerService.findDirectMessages({ receiverId, filter }).pipe(
          map((channelMessages) => AIAssistantActions.GetChannelMessagesByTimestampSuccessful({
            payload: { channelId: 'private', channelMessages }
          })),
          catchError((error) =>
            of(AIAssistantActions.Error({ payload: { error: { error, key: 'GetDirectMessagesByTimestamp' } } }))
          )
        );
      })
    )
  );

  sendDirectMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AIAssistantActions.SendDirectMessage),
      withLatestFrom(this.store.select(selectActiveProjectId)),
      mergeMap(([action, activeProjectId]) => {
        const { message, receiverId: payloadReceiverId, tempId } = action.payload;
        const receiverId = payloadReceiverId || RAPIDER_AI_RECEIVER_ID;

        return this.projectChannelMessageControllerService
          .sendDirectMessage({
            body: {
              ...message,
              receiverId,
            },
          })
          .pipe(
            map((response) =>
              AIAssistantActions.SendDirectMessageSuccessful({
                payload: {
                  userMessage: response.message,
                  aiMessage: response.aiMessage,
                  receiverId: receiverId,
                  tempId: tempId, // <-- we are passing the tempId through to the success action
                },
              })
            ),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                error?.statusText,
                error?.error?.error?.message
              );
              return of(
                AIAssistantActions.Error({
                  payload: { error: { error, key: 'SendDirectMessage' } },
                })
              )
            }

            )
          );
      })
    )
  );


  reloadChatMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthenticationActions.UpdateAuthenticationTokenWithProjectId>(
        AuthenticationActions.ActionTypes.UpdateAuthenticationTokenWithProjectId,
      ),
      map(() => AIAssistantActions.GetChannelMessagesByTimestamp({ payload: { timestamp: new Date().toISOString() } }))
    )
  );

  getChannelMessagesByTimestamp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AIAssistantActions.GetChannelMessagesByTimestamp
      ),
      withLatestFrom(
        this.store.select(selectActiveProjectId),
        this.store.select(state => state.aiAssistant.channels)
      ),
      mergeMap(([action, activeProjectId, channels]) => {
        const defaultChannelInState = channels?.find((c: ProjectChannel) => c.isDefault);
        if (!defaultChannelInState) {
          // If there is no default channel in the state get it from the API
          return this.projectChannelControllerService.find({ filter: { where: { isDefault: true } } }).pipe(
            mergeMap((foundChannels: ProjectChannel[]) => {
              // eslint-disable-next-line no-constant-condition
              if (foundChannels?.length > 0) {
                const channel = foundChannels[0];
                this.store.dispatch(AIAssistantActions.SetProjectChannels({ payload: { channels: foundChannels } }));
                return this.projectChannelMessageControllerService.findChannelMessages({
                  channelId: channel.id,
                  filter: {
                    where: {
                      createdDate: {
                        lte: action.payload?.timestamp || new Date().toISOString()
                      }
                    },
                    order: ['createdDate DESC'],
                    limit: 20
                  }
                }).pipe(
                  map((channelMessages) => AIAssistantActions.GetChannelMessagesByTimestampSuccessful({
                    payload: { channelId: channel.id, channelMessages }
                  })),
                  catchError((error) =>
                    of(AIAssistantActions.Error({ payload: { error: { error, key: 'GetChannelMessagesByTimestamp' } } }))
                  )
                );
              } else {
                return of(AIAssistantActions.Error({ payload: { error: { error: 'No default channel found.', key: 'GetChannelMessagesByTimestamp' } } }));
              }
            }));
        } else {
          // If there is a default channel in the state, use it
          const channel = defaultChannelInState;
          return this.projectChannelMessageControllerService.findChannelMessages({
            channelId: channel.id,
            filter: {
              where: {
                createdDate: {
                  lte: action.payload?.timestamp || new Date().toISOString()
                }
              },
              order: ['createdDate DESC'],
              limit: 20
            }
          }).pipe(
            map((channelMessages) => AIAssistantActions.GetChannelMessagesByTimestampSuccessful({
              payload: { channelId: channel.id, channelMessages }
            })),
            catchError((error) =>
              of(AIAssistantActions.Error({ payload: { error: { error, key: 'GetChannelMessagesByTimestamp' } } }))
            )
          );
        }
      })
    )
  );

  /* Get Direct Messages by Timestamp Effect */

  createAIFunction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AIAssistantActions.CreateAIFunction
      ),
      withLatestFrom(
        this.store.select(selectActiveProjectId),
      ),
      mergeMap(([action, activeProjectId]) => {
        const params = {
          body: {
            ...action.payload.aiFunctionCall,
            name: action.payload.aiFunctionCall?.name || '',
            functionPrompt: action.payload.aiFunctionCall?.functionPrompt || '',
            responseJsonSample: action.payload.aiFunctionCall?.responseJsonSample || '',
            systemPrompt: action.payload.aiFunctionCall?.systemPrompt || '',
            projectId: activeProjectId
          }
        };
        return this.aiFunctionCallControllerService.create$FormData(params).pipe(
          mergeMap((aiFunctionCall: AiFunctionCall) => ([AIAssistantActions.CreateAIFunctionSuccessful({ payload: { aiFunctionCall } })])),
          catchError((error) =>
            [
              AIAssistantActions.Error({ payload: { error: { error, key: 'CreateAIFunction' } } })
            ]
          )
        );
      })
    )
  );

  analyzeDataSources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AIAssistantActions.AnalyzeDataSource
      ),
      mergeMap((action) => {
        const params = {
          body: {
            dataSourceIds: action.payload.dataSourceIds,
            message: action.payload.message
          }
        }
        return this.aiFunctionCallControllerService.analyzeDataSource$Response(params).pipe(
          map((response) => {
            return AIAssistantActions.AnalyzeDataSourceSuccessful({ payload: { dataSourceIds: action.payload.dataSourceIds, message: action.payload.message, correlationId: response.body.correlationId || '' } });
          }), catchError(error => {
            return [
              AIAssistantActions.AnalyzeDataSourceFailure({ payload: { error, key: 'AnalyzeDataSource', timestamp: Date.now(), message: action.payload.message } })
            ];
          }
          ));
      }
      )
    )
  );

}
